<template>
  <v-container>
    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col
        cols="12"
        class="d-flex align-center">
        <h2 class="text-h4 font-weight-bold">
          Warehouse Work Type
        </h2>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          elevation="2"
          @click="openDialog()">
          <v-icon left>
            mdi-plus
          </v-icon> เพิ่มประเภทงาน
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-card elevation="2">
      <v-data-table
        :headers="headers"
        :items="workTypes"
        :options.sync="options"
        :items-per-page="options.itemsPerPage"
        :server-items-length="total"
        :loading="loading"
        :footer-props="{'items-per-page-options': [5, 15, 25, 50]}"
        :sort-by="['displayOrder']"
        class="elevation-0"
        @update:options="fetchTaskTypes()"
      >
        <!-- Custom Column: Requires Ref -->
        <template v-slot:item.requiresRef="{ item }">
          <v-chip
            :color="item.requiresRef ? 'orange lighten-4' : 'grey lighten-3'"
            :text-color="item.requiresRef ? 'orange darken-4' : 'grey darken-2'"
            small
            class="font-weight-medium"
          >
            {{ item.requiresRef ? 'Requires Ref' : 'No Ref' }}
          </v-chip>
        </template>

        <!-- Custom Column: Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="item.status === 'active' ? 'success' : 'error'"
            dark
            x-small
            label
          >
            {{ item.status === 'active' ? 'ACTIVE' : 'INACTIVE' }}
          </v-chip>
        </template>

        <!-- Custom Column: Actions -->
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="mr-3"
            color="blue darken-1"
            @click="openDialog(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            class="mr-2"
            color="red"
            @click="toggleStatus(item)">
            {{ item.status === 'active' ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline' }}
          </v-icon>
          <v-icon
            small
            color="red darken-1"
            @click="deleteItem(item)"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Form (Add/Edit) -->
    <v-dialog
      v-model="dialog"
      max-width="600px"
      persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ formTitle }}
        </v-card-title>

        <v-card-text class="pt-5">
          <v-form ref="form">
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  md="6">
                  <v-text-field
                    v-model="editedItem.name"
                    label="ชื่อประเภทงาน (Name)"
                    dense
                    outlined
                    :rules="[
                      (v) => !!v || 'กรุณากรอกชื่อประเภทงาน'
                    ]"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6">
                  <v-select
                    v-model="editedItem.department"
                    :items="departments"
                    label="แผนก"
                    dense
                    outlined
                    :rules="[
                      (v) => !!v || 'กรุณาเลือกแผนก'
                    ]"></v-select>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model.number="editedItem.displayOrder"
                    type="number"
                    label="ลำดับการแสดงผล"
                    dense
                    outlined
                    :rules="[
                      (v) => (v !== null && v !== undefined && v !== '') || 'กรุณากรอกลำดับการแสดงผล',
                      (v) => Number(v) > 0 || 'กรอกได้เฉพาะตัวเลขจำนวนเต็มบวก',
                      (v) => Number.isInteger(Number(v)) || 'กรอกได้เฉพาะตัวเลขจำนวนเต็มเท่านั้น'
                    ]"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-model="editedItem.requiresRef"
                    :items="[{ text: 'YES', value: true }, { text: 'NO', value: false }]"
                    label="ต้องการเอกสารอ้างอิง (Requires Ref)"
                    outlined
                    dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="close">
            ยกเลิก
          </v-btn>
          <v-btn
            color="primary"
            depressed
            @click="save">
            บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Toggle Status Dialog -->
    <v-dialog
      v-model="confirmStatusDialog"
      max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          ยืนยันการเปลี่ยนสถานะ
        </v-card-title>
        <v-card-text>
          คุณต้องการที่จะ <strong>{{ selectedItem.status === 'active' ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน' }}</strong>
          ประเภทงาน <strong>"{{ selectedItem.name }}"</strong> ใช่หรือไม่?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="confirmStatusDialog = false">
            ยกเลิก
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="togglingStatus"
            @click="confirmToggleStatus">
            ยืนยัน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog
      v-model="dialogDelete"
      max-width="450px">
      <v-card>
        <v-card-title class="text-h6">
          ยืนยันการลบข้อมูล
        </v-card-title>
        <v-card-text class="tpb-0">
          คุณแน่ใจหรือไม่ว่าต้องการลบงานประเภท
          <span class="font-weight-bold red--text">"{{ editedItem.name }}"</span>?
          <br>การดำเนินการนี้ไม่สามารถเรียกคืนได้
        </v-card-text>
        <v-card-actions class="pb-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeDelete">
            ยกเลิก
          </v-btn>
          <v-btn
            color="red darken-1"
            dark
            depressed
            @click="deleteItemConfirm">
            ยืนยันลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'
import { mapActions } from 'vuex'

const warehouseWorkTrackingProvider = new WarehouseWorkTrackingProvider()

export default {
  data: () => ({
    dialog: false,
    dialogDelete: false,
    confirmStatusDialog: false,
    togglingStatus: false,
    selectedItem: {},
    loading: false,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['displayOrder'],
      sortDesc: [false]
    },
    total: 0,
    headers: [
      { text: 'ลำดับ', value: 'displayOrder', align: 'start', width: '100px', sortable: false },
      { text: 'ประเภทงาน', value: 'name', sortable: false },
      { text: 'แผนกที่รับผิดชอบ', value: 'department', sortable: false },
      { text: 'การอ้างอิง', value: 'requiresRef', align: 'center', sortable: false },
      { text: 'สถานะ', value: 'status', align: 'center', sortable: false },
      { text: 'เครื่องมือ', value: 'actions', sortable: false, align: 'right' }
    ],
    workTypes: [],
    departments: ['Inbound', 'Outbound', 'Inventory', 'Return', 'Other'],
    editedIndex: -1,
    editedItem: {
      id: null,
      name: '',
      department: '',
      requiresRef: false,
      displayOrder: 0,
      status: 'active'
    },
    defaultItem: {
      id: null,
      name: '',
      department: '',
      requiresRef: false,
      displayOrder: 0,
      status: 'active'
    }
  }),

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? 'เพิ่ม Warehouse Work Type' : 'แก้ไข Warehouse Work Type'
    }
  },

  created () {
    this.fetchTaskTypes()
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage'
    }),
    async fetchTaskTypes () {
      this.loading = true
      try {
        const { data: { results, total } } = await warehouseWorkTrackingProvider.getTaskTypes({
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
        })
        this.workTypes = results || []
        this.total = total
      } catch (err) {
        console.error('Failed to fetch task types:', err)
        this.setSnackbar({
          value: true,
          message: 'ไม่สามารถโหลดข้อมูลประเภทงานได้',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    openDialog (item) {
      this.editedIndex = item ? this.workTypes.indexOf(item) : -1
      this.editedItem = { ...item || this.defaultItem }
      this.dialog = true
    },

    toggleStatus (item) {
      this.selectedItem = item
      this.confirmStatusDialog = true
    },

    async confirmToggleStatus () {
      this.togglingStatus = true
      try {
        const newStatus = this.selectedItem.status === 'active' ? 'inactive' : 'active'
        await warehouseWorkTrackingProvider.updateTaskTypeStatus(this.selectedItem.id, { status: newStatus })
        const index = this.workTypes.indexOf(this.selectedItem)
        if (index > -1) {
          this.workTypes[index].status = newStatus
        }
        this.setSnackbar({
          value: true,
          message: `${newStatus === 'active' ? 'เปิด' : 'ปิด'}การใช้งานเรียบร้อย`,
          type: 'success'
        })
      } catch (err) {
        console.error('Failed to toggle status:', err)
        this.setSnackbar({
          value: true,
          message: 'ไม่สามารถเปลี่ยนสถานะได้',
          type: 'error'
        })
      } finally {
        this.togglingStatus = false
        this.confirmStatusDialog = false
      }
    },

    deleteItem (item) {
      this.editedIndex = this.workTypes.indexOf(item)
      this.editedItem = { ...item }
      this.dialogDelete = true
    },

    async deleteItemConfirm () {
      try {
        await warehouseWorkTrackingProvider.deleteTaskType(this.editedItem.id)
        this.setSnackbar({
          value: true,
          message: 'ลบประเภทงานเรียบร้อย',
          type: 'success'
        })
        await this.fetchTaskTypes()
      } catch (err) {
        console.error('Failed to delete task type:', err)
        this.setSnackbar({
          value: true,
          message: 'ไม่สามารถลบประเภทงานได้',
          type: 'error'
        })
      } finally {
        this.closeDelete()
      }
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    async save () {
      if (!this.$refs.form.validate()) return
      try {
        const payload = {
          name: this.editedItem.name,
          department: this.editedItem.department,
          requiresRef: this.editedItem.requiresRef,
          displayOrder: this.editedItem.displayOrder,
          status: this.editedItem.status
        }

        if (this.editedIndex > -1) {
          await warehouseWorkTrackingProvider.updateTaskType(this.editedItem.id, payload)
          this.setSnackbar({
            value: true,
            message: 'แก้ไขประเภทงานเรียบร้อย',
            type: 'success'
          })
        } else {
          await warehouseWorkTrackingProvider.createTaskType(payload)
          this.setSnackbar({
            value: true,
            message: 'เพิ่มประเภทงานเรียบร้อย',
            type: 'success'
          })
        }
        this.close()
        await this.fetchTaskTypes()
      } catch (err) {
        console.error('Failed to save task type:', err)
        this.setSnackbar({
          value: true,
          message: err.message || 'ไม่สามารถบันทึกข้อมูลได้',
          type: 'error'
        })
      }
    }
  }
}
</script>

<style scoped>
/* คุณสามารถปรับแต่ง CSS เพิ่มเติมได้ที่นี่ */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}
</style>
